import {
  addItemToCart,
  applyCoupon,
  deleteCartItem,
  deleteCoupon,
  getCoupons,
  updateCartItem,
} from '@/services/cart';
import useCart from '@/store/cart.store';
import useCheckout from '@/store/checkout.store';
import { getNeededProductData } from '@/utils/global';
import {
  event_add_to_cart,
  event_legacy_quantity_decreased,
  event_legacy_quantity_increased,
  event_coupon_code,
  event_coupon_code_failed,
  event_coupon_redemption,
  event_errors,
  event_remove_from_cart,
  event_update_cart,
} from '@/utils/gtm';
import { getFailedStatus } from '@/utils/gtm/helper';
import { getUnavailableSkus } from '@/utils/gtm/item-lists';
import { getCookie, setCookie } from 'cookies-next';
import { useSession } from 'next-auth/react';
import { useLocale } from 'next-intl';
import { useState } from 'react';
import { toast } from '@/utils/toast';

import { setShippingAddress } from '@/services/checkout';
import useShippingInfoStore from '@/store/shipping-info.store';

const useHandleCart = (refetch) => {
  const { data: session } = useSession();
  const resetCart = useCart((store) => store.resetCart);
  const removeItem = useCart((store) => store.removeItem);
  const refetchCart = useCart((store) => store.refetchCart);
  const addItem = useCart((store) => store.addItem);
  // const setTotalSegments = useCart((state) => state.setTotalSegments);
  const updateItem = useCart((store) => store.updateItem);
  const cart = useCart((store) => store.cart);
  const clearCheckout = useCheckout((state) => state.clearCheckout);
  const checkoutState = useCheckout((state) => state);

  const shippingAddress = useShippingInfoStore(
    (state) => state.shippingAddress
  );
  const shippingMethod = useShippingInfoStore((state) => state.shippingMethod);
  const unavailableItems = useShippingInfoStore(
    (state) => state.unavailableItems
  );

  const quote = getCookie('quote');
  const mage = getCookie('mage');
  const [loading, setLoading] = useState(null);
  const locale = useLocale();

  const [couponsData, setCouponsData] = useState([]);

  const deleteItemFromCart = async (product) => {
    setLoading('deleting');
    const res = await deleteCartItem(product?.item_id, mage, quote);

    if (res?.status === 404) {
      removeItem(product?.item_id);
    }

    if (res?.data?.quoteId !== undefined && res?.data?.quoteId != quote) {
      setCookie('quote', res?.data?.quoteId);
      resetCart();
    }
    if (res?.status === 200) {
      if (product) {
        // Delete takes the whole line, so the delta is its full quantity.
        event_remove_from_cart({
          product,
          quantity: product?.qty || 1,
          locale,
          unavailableSkus: getUnavailableSkus(unavailableItems),
        });
      }
      removeItem(product?.item_id);

      refetchCart && (await refetchCart());
      refetch && (await refetch());
      setLoading('');
      toast.success(
        locale === 'en' ? 'Item Deleted From Cart' : 'تم إزالة المنتج من السلة'
      );
    } else {
      setLoading('');
      toast.error(
        locale === 'en'
          ? 'Error Occured, please try again'
          : 'حدث خطأ, برجاء المحاولة مره أخرى'
      );
    }
  };

  const handleUpdateCart = async (is_minus, item_id, qty, mainProduct) => {
    !is_minus ? setLoading('adding') : setLoading('subtracting');

    const res = await updateCartItem(
      item_id,
      is_minus ? qty - 1 : qty + 1,
      mage,
      quote
    );
    if (res?.status === 404) {
      removeItem(item_id);
    }
    // setTotalSegments(res?.data?.cart_details?.total_segments);

    if (res?.data?.quoteId !== undefined && res?.data?.quoteId != quote) {
      setCookie('quote', res?.data?.quoteId);
      resetCart();
    }
    if (res?.status === 200) {
      if (refetchCart) {
        const refetchCartRes = await refetchCart();
        if (refetchCartRes?.data?.data) {
          event_update_cart({ cartData: refetchCartRes?.data?.data });
        }
      }

      if (refetch) {
        const refetchRes = await refetch();
        if (refetchRes?.data?.data) {
          event_update_cart({ cartData: refetchRes?.data?.data });
        }
      }

      setLoading(null);
      updateItem(item_id, res?.data?.data?.qty);

      if (mainProduct) {
        if (is_minus) {
          // The stepper removes exactly one unit.
          event_remove_from_cart({
            product: mainProduct,
            quantity: 1,
            locale,
            unavailableSkus: getUnavailableSkus(unavailableItems),
          });
          event_legacy_quantity_decreased({
            product: mainProduct,
            is_minus: true,
            total_cart_value: cart?.grand_total,
          });
        } else {
          // The stepper adds exactly one unit. The spec wants that delta, not
          // the resulting cart quantity.
          event_add_to_cart({ product: mainProduct, quantity: 1, locale });
          event_legacy_quantity_increased({
            product: mainProduct,
            qty: qty + 1,
            is_plus: true,
            total_cart_value: cart?.grand_total,
          });
        }
      }

      toast.success(
        locale === 'en' ? 'Cart updated successfully' : 'تم تعديل الكمية بنجاح'
      );
    } else {
      toast.error(
        locale === 'en' ? 'No quantities available' : 'لا توجد كميات متاحة'
      );
      setLoading(null);
    }
  };

  const handleAddToCart = async (product, qty = 1, t) => {
    setLoading('adding');

    try {
      const res = await addItemToCart({
        sku: Array.isArray(product?.sku) ? product?.sku[0] : product?.sku,
        qty,
        quote,
        mage,
      });

      if (res?.status === 200) {
        // Handle new quote
        if (quote !== res?.data?.quoteId) {
          resetCart();
          clearCheckout();
          setCookie('quote', res?.data?.quoteId);
        }

        // Now add to cart with related products

        const productData = getNeededProductData(product);

        addItem({
          ...productData,
          ...res?.data?.data,
          qty: res?.data?.data?.qty,
          productFeesFromCart: [],
        });

        // Covers the PDP stepper's + once the product is already in the cart,
        // and the cart pop-up's suggestion tiles. Both add one unit per press.
        event_add_to_cart({ product, quantity: qty, locale });
      } else {
        toast.error(
          t(res?.error?.error?.message?.replaceAll('.', '') || 'ErrorAddToCart')
        );
      }
    } catch (e) {
      toast.error(t('ErrorAddToCart'));
      console.error(e);
    }

    setLoading('');
  };

  const refreshCheckoutShippingAddress = async () => {
    if (shippingAddress?.region_id) {
      const updatedShippingAddress = { ...shippingAddress };
      delete updatedShippingAddress.region;
      delete updatedShippingAddress.default_billing;
      delete updatedShippingAddress.default_shipping;
      delete updatedShippingAddress.id;

      updatedShippingAddress.region =
        shippingAddress?.region?.region || shippingAddress?.region_id;
      updatedShippingAddress.telephone = session?.user?.mobilenumber;
      updatedShippingAddress.email = session?.user?.email;
      updatedShippingAddress.firstname = session?.user?.firstname;
      updatedShippingAddress.country_id = 'SA';

      const res = await setShippingAddress(
        mage,
        quote,
        updatedShippingAddress,
        checkoutState?.activeShippingMethod,
        shippingMethod === 'storepickup' ? shippingAddress?.id : null,
        locale
      );
      if (res?.status === 200 && !res?.error) {
        checkoutState.setCart(res?.data?.data?.totals);
        checkoutState.setPayments(res?.data?.data?.payment_methods);
      }
    }
  };

  const applyCouponHandler = async (coupon) => {
    setLoading('apply_coupon');
    const res = await applyCoupon(mage, quote, coupon);
    const isCheckout =
      typeof window !== 'undefined' &&
      window.location.pathname.includes('/checkout');
    const redemption_location = isCheckout ? 'checkout' : 'cart';

    if (res?.status === 200) {
      if (isCheckout && shippingAddress?.region_id) {
        await refreshCheckoutShippingAddress();
      }
      let refetchRes = null;
      if (refetchCart) {
        refetchRes = await refetchCart();
      }
      if (refetch) {
        refetchRes = await refetch();
      }
      // refetchCart && (await refetchCart());
      // refetch && (await refetch());

      setLoading('');
      toast.success(
        locale === 'en'
          ? 'Coupon applied successfully'
          : 'تم تطبيق الكوبون بنجاح'
      );
      setCookie('coupon_code', refetchRes?.data?.data?.coupon_code);
      setCookie('coupon_amount', refetchRes?.data?.data?.discount_amount);
      event_coupon_code({
        coupon_code: refetchRes?.data?.data?.coupon_code,
        coupon_discount: refetchRes?.data?.data?.discount_amount,
        grand_total: refetchRes?.data?.data?.grand_total,
        event_name: 'coupon_code_applied',
      });
      event_coupon_redemption({
        code: coupon,
        redemption_result: 'Success',
        redemption_location,
      });
    } else {
      setLoading('');
      toast.error(locale === 'en' ? 'Invalid Coupon' : 'كوبون غير صحيح');
      event_errors({
        error_message: 'Invalid Coupon',
        error_type: 'API Error',
      });
      event_coupon_code_failed({
        coupon_code: coupon,
        grand_total: cart?.grand_total,
        reason: locale === 'en' ? 'Invalid Coupon' : 'كوبون غير صحيح',
      });
      event_coupon_redemption({
        code: coupon,
        redemption_result: getFailedStatus(res?.error?.error, 'Invalid Coupon'),
        redemption_location,
      });
    }
  };

  const removeCouponHandler = async () => {
    setLoading('remove_coupon');
    const res = await deleteCoupon(mage, quote);
    if (res?.status === 200) {
      const isCheckout =
        typeof window !== 'undefined' &&
        window.location.pathname.includes('/checkout');
      if (isCheckout && shippingAddress?.region_id) {
        await refreshCheckoutShippingAddress();
      }
      let refetchRes = null;
      if (refetchCart) {
        refetchRes = await refetchCart();
      }
      if (refetch) {
        refetchRes = await refetch();
      }
      // refetchCart && (await refetchCart());
      // refetch && (await refetch());

      setLoading('');
      event_coupon_code({
        coupon_code: getCookie('coupon_code'),
        coupon_discount: getCookie('coupon_amount'),
        grand_total: refetchRes?.data?.data?.grand_total,
        event_name: 'coupon_code_removed',
      });
      toast.success(
        locale === 'en' ? 'Coupon removed successfully' : 'تم حذف الكوبون بنجاح'
      );
    } else {
      setLoading('');
      event_errors({
        error_message: 'Coupon removal failed',
        error_type: 'API Error',
      });
      toast.error(locale === 'en' ? 'Invalid Coupon' : 'كوبون غير صحيح');
      event_coupon_code_failed({
        coupon_code: getCookie('coupon_code'),
        grand_total: cart?.grand_total,
        reason:
          locale === 'en'
            ? 'Something went wrong, please try again'
            : 'حدث خطأ ما , برجاء المحاولة مره أخرى',
      });
    }
  };

  const getCouponsHandler = async () => {
    setLoading('get_coupons');
    const res = await getCoupons(mage);
    if (res?.status === 200) {
      setLoading('');
      const data = res?.data?.data?.filter((coupon) => !coupon?.mobile_only);
      setCouponsData(data);
    } else {
      setLoading('');
    }
  };

  return {
    deleteItemFromCart,
    loading,
    handleUpdateCart,
    applyCouponHandler,
    removeCouponHandler,
    handleAddToCart,
    getCouponsHandler,
    couponsData,
  };
};

export default useHandleCart;
